import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import { CreateLineSystem, CreateLines } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import "@babylonjs/core/Meshes/Builders/ribbonBuilder"; //side effect for VertexData.CreateRibbon
import { SnapLine } from "../../core/tool/SnapLine";
import { normalizeAngle, quaternionFromAxes, rotateVector3ToRef, signedAngleBetweenVectors3, VECTOR3_X, VECTOR3_Y, VECTOR3_Z } from "../../core/utils/MathUtils";
import { resizeInViewSpace } from "../../core/utils/MeshUtils";
import { CoordinatesType, ViewLayerMaskFlags } from "../../core/view/View";
import { degToRad, PI_MUL2, radToDeg } from "../../utils/math";
import { AbstractRotate } from "./AbstractRotate";
export const RotationAxis = {
    X: 0,
    Y: 1,
    Z: 2
};
export class DefaultRotate extends AbstractRotate {
    constructor(pApp) {
        super(pApp);
        this.onCameraChanged = () => {
            this.updateFeedbackSize();
            this.mApp.viewer.requestRenderFrame();
        };
        this.mStartPoint = Vector3.Zero();
        this.mLastPickPos = Vector3.Zero();
        this.mCurrentSnapAxis = null;
        this.mCircleFeedback = null;
        this.mLineFeedback = null;
        this.mArcFeedback = null;
        this.mArcFeedbackOverlay = null;
        this.mArcFeedbackMat = null;
        this.mLineFeedbackPosData = null;
        this.mArcFeedbackOffset = 0;
        this.mValueDiv = null;
        this.mSnapAxes = new Array();
    }
    start(pParameter) {
        const tResult = super.start(pParameter);
        if (!tResult)
            return (tResult);
        this.mCurrentSnapAxis = null;
        this.mLastPickPos = this.mStartPoint;
        this.prepareFeedbackAndSnapping();
        this.mView.cameraControl.eventCameraChanged.addListener(this.onCameraChanged);
        return (true);
    }
    stop() {
        if (this.mCircleFeedback != null) {
            this.mCircleFeedback.dispose();
            this.mCircleFeedback = null;
        }
        if (this.mLineFeedback != null) {
            this.mLineFeedback.dispose();
            this.mLineFeedback = null;
        }
        if (this.mArcFeedback != null) {
            this.mArcFeedback.dispose();
            this.mArcFeedback = null;
        }
        if (this.mArcFeedbackMat != null) {
            this.mArcFeedbackMat.dispose();
            this.mArcFeedbackMat = null;
        }
        if (this.mValueDiv != null) {
            this.mView.overlayDiv.removeChild(this.mValueDiv);
            this.mValueDiv = null;
        }
        this.mView.cameraControl.eventCameraChanged.removeListener(this.onCameraChanged);
        super.stop();
    }
    handlePointerMove(pEvent, pState) {
        if (this.mCommand == null || !pState.isPrimaryPointer(pEvent))
            return;
        const tPointer = pState.pointers.get(pEvent.pointerId);
        if (tPointer == null)
            return;
        let tCurrentPos = this.getPlanePickPos(tPointer.position);
        tCurrentPos = this.snapOnAxis(tCurrentPos);
        if (tCurrentPos == null || this.mPickPlane == null)
            return;
        const tP1 = tCurrentPos.subtract(this.mRefPoint).normalize();
        const tP2 = this.mLastPickPos.subtract(this.mRefPoint).normalize();
        let tAngle = signedAngleBetweenVectors3(tP1, tP2, this.mPickPlane.normal);
        //1° degree snapping
        if (this.mCurrentSnapAxis == null) {
            const tDegAngle = Math.round(radToDeg(tAngle));
            const tNewAngle = degToRad(tDegAngle);
            const tDifAngle = tNewAngle - tAngle;
            tAngle = tNewAngle;
            //adjust tCurrentPos
            const tRot = Quaternion.RotationAxis(this.mPickPlane.normal, tDifAngle);
            const tVCurrent = tCurrentPos.subtract(this.mRefPoint);
            rotateVector3ToRef(tVCurrent, tRot, tVCurrent);
            this.mRefPoint.addToRef(tVCurrent, tCurrentPos);
        }
        this.mTotalAngle += tAngle;
        this.mTotalAngle = normalizeAngle(this.mTotalAngle, true);
        const tRot = Quaternion.RotationAxis(this.mAxisVector, tAngle);
        this.mCommand.rotate(tRot, this.mLocalRotation, this.mTransformedRefPoint);
        this.updateFeedback();
        this.mLastPickPos = tCurrentPos;
    }
    /**
     * pRefPoint in world coordinates.
     */
    initialize(pRefPoint, pStartPoint, pAxis) {
        this.mRefPoint = pRefPoint.clone();
        this.mStartPoint = pStartPoint.clone();
        switch (pAxis) {
            case RotationAxis.X:
                this.mAxisVector = VECTOR3_X;
                break;
            case RotationAxis.Y:
                this.mAxisVector = VECTOR3_Y;
                break;
            case RotationAxis.Z:
                this.mAxisVector = VECTOR3_Z;
                break;
        }
    }
    prepareFeedbackAndSnapping() {
        if (this.mPickPlane == null)
            return;
        //build coordinate system for snap axis
        let tAxisX = null;
        let tAxisY = null;
        const tAxisZ = this.mPickPlane.normal;
        const tDotX = Math.abs(Vector3.Dot(VECTOR3_X, tAxisZ));
        const tDotY = Math.abs(Vector3.Dot(VECTOR3_Y, tAxisZ));
        const tDotZ = Math.abs(Vector3.Dot(VECTOR3_Z, tAxisZ));
        if (tDotZ > tDotX && tDotZ > tDotY) //z axis is nearest
            tAxisX = VECTOR3_X;
        else if (tDotY > tDotX && tDotY > tDotZ) //y axis is nearest
            tAxisX = VECTOR3_Z;
        else
            tAxisX = VECTOR3_Y;
        tAxisY = Vector3.Cross(tAxisX, tAxisZ).normalize();
        tAxisX = Vector3.Cross(tAxisY, tAxisZ);
        //setup snap axes
        this.mSnapAxes = new Array();
        this.mSnapAxes.push(SnapLine.FromAxis(this.mRefPoint, tAxisX));
        this.mSnapAxes.push(SnapLine.FromAxis(this.mRefPoint, tAxisY));
        this.mSnapAxes.push(SnapLine.FromAxis(this.mRefPoint, Vector3.Center(tAxisX, tAxisY)));
        this.mSnapAxes.push(SnapLine.FromAxis(this.mRefPoint, Vector3.Center(tAxisX, tAxisY.negate())));
        //create circle feedback
        const tLines = new Array();
        const tSteps = 72;
        const tStepSize = PI_MUL2 / tSteps;
        const tCirclePoints = new Array();
        tLines.push(tCirclePoints);
        for (let i = 0; i < tSteps; ++i) {
            const tX = Math.cos(i * tStepSize);
            const tY = Math.sin(i * tStepSize);
            let tR = 0.9;
            if (i % 18 === 0) //90°
                tR = 0.8;
            else if (i % 9 === 0) //45°
                tR = 0.85;
            const tV1 = new Vector3(tX, tY, 0);
            const tV2 = new Vector3(tX * tR, tY * tR, 0);
            tCirclePoints.push(tV1);
            tLines.push([tV1, tV2]);
        }
        tCirclePoints.push(tCirclePoints[0]);
        this.mCircleFeedback = CreateLineSystem("", {
            lines: tLines, updatable: false, instance: null
        }, this.mApp.model.scene);
        this.mCircleFeedback.isPickable = false;
        this.mCircleFeedback.color = Color3.FromHexString("#868686");
        this.mCircleFeedback.layerMask = ViewLayerMaskFlags.All & ~ViewLayerMaskFlags.Screenshot;
        this.mCircleFeedback.rotationQuaternion = quaternionFromAxes(tAxisX, tAxisY, tAxisZ);
        this.mCircleFeedback.position = this.mRefPoint;
        const tCircleOverlay = this.mCircleFeedback.clone("", this.mCircleFeedback);
        tCircleOverlay.renderingGroupId = 1;
        tCircleOverlay.alpha = 0.5;
        tCircleOverlay.rotationQuaternion = null;
        tCircleOverlay.position = Vector3.Zero();
        tCircleOverlay.scaling.copyFromFloats(1, 1, 1);
        //line feedback
        const tDummyPos = Vector3.Zero();
        this.mLineFeedback = CreateLines("", { points: [tDummyPos, tDummyPos], updatable: true, instance: null, useVertexAlpha: false }, this.mApp.model.scene);
        this.mLineFeedback.color = Color3.FromHexString(this.mApp.theme.mainColorBright);
        this.mLineFeedback.renderingGroupId = 2;
        this.mLineFeedback.layerMask = ViewLayerMaskFlags.All & ~ViewLayerMaskFlags.Screenshot;
        this.mLineFeedback.isPickable = false;
        this.mLineFeedback.position = this.mRefPoint;
        this.mLineFeedbackPosData = this.mLineFeedback.getVerticesData(VertexBuffer.PositionKind, false);
        //arc feedback
        this.mArcFeedbackMat = new PBRMaterial("", this.mApp.model.scene);
        this.mArcFeedbackMat.albedoColor = Color3.Black();
        this.mArcFeedbackMat.emissiveColor = Color3.FromHexString(this.mApp.theme.mainColorBright).toLinearSpace();
        this.mArcFeedbackMat.disableLighting = true;
        this.mArcFeedbackMat.unlit = true;
        this.mArcFeedbackMat.alpha = 0.3;
        this.mArcFeedbackMat.backFaceCulling = false;
        this.mArcFeedback = new Mesh("", this.mApp.model.scene);
        this.mArcFeedback.material = this.mArcFeedbackMat;
        this.mArcFeedback.layerMask = ViewLayerMaskFlags.All & ~ViewLayerMaskFlags.Screenshot;
        this.mArcFeedback.isPickable = false;
        this.mArcFeedback.position = this.mRefPoint;
        this.mArcFeedback.rotationQuaternion = this.mCircleFeedback.rotationQuaternion;
        this.mArcFeedbackOverlay = this.mArcFeedback.clone("", this.mArcFeedback);
        this.mArcFeedbackOverlay.renderingGroupId = 1;
        this.mArcFeedbackOverlay.visibility = 0.3;
        this.mArcFeedbackOverlay.rotationQuaternion = null;
        this.mArcFeedbackOverlay.position = Vector3.Zero();
        this.mArcFeedbackOverlay.scaling.copyFromFloats(1, 1, 1);
        const tDir = this.mStartPoint.subtract(this.mRefPoint);
        tDir.normalize();
        this.mArcFeedbackOffset = signedAngleBetweenVectors3(tDir, tAxisX, this.mPickPlane.normal);
        //value div
        this.mValueDiv = document.createElement("div");
        this.mValueDiv.className = "wcf-core length-feedback";
        this.mValueDiv.style.visibility = "hidden";
        //Workaround for android app (WebView). Inside the webview the div doesn't get redrawn correctly
        //making the div slightly transparent avoids this issue.
        this.mValueDiv.style.opacity = "0.999";
        const tProjPos = this.mView.projectPoint2(this.mRefPoint, CoordinatesType.Viewer);
        if (tProjPos != null) {
            setTimeout(() => {
                if (this.mValueDiv == null)
                    return;
                this.mValueDiv.style.visibility = "visible";
                this.mValueDiv.style.top = Math.round(tProjPos.y - this.mValueDiv.offsetHeight * 0.5) + "px";
                this.mValueDiv.style.left = Math.round(tProjPos.x - this.mValueDiv.offsetWidth * 0.5) + "px";
            }, 10);
        }
        this.mView.overlayDiv.appendChild(this.mValueDiv);
        this.updateFeedbackSize();
        this.updateFeedback();
    }
    updateFeedbackSize() {
        if (this.mCircleFeedback != null) {
            //scale circle to have half size of the view
            const tPixelRadius = Math.min(this.mView.width, this.mView.height) * 0.25;
            resizeInViewSpace(this.mView, this.mCircleFeedback, 1, tPixelRadius);
            if (this.mLineFeedback != null) {
                this.mLineFeedback.scaling = this.mCircleFeedback.scaling;
            }
            if (this.mArcFeedback != null) {
                this.mArcFeedback.scaling = this.mCircleFeedback.scaling;
            }
        }
    }
    updateFeedback() {
        if (this.mPickPlane == null)
            return;
        const tDir = this.mStartPoint.subtract(this.mRefPoint);
        tDir.normalize();
        const tRot = Quaternion.RotationAxis(this.mPickPlane.normal, this.mTotalAngle);
        rotateVector3ToRef(tDir, tRot, tDir);
        //update line feedback
        if (this.mLineFeedback != null && this.mLineFeedbackPosData != null) {
            this.mLineFeedbackPosData[3] = tDir.x;
            this.mLineFeedbackPosData[4] = tDir.y;
            this.mLineFeedbackPosData[5] = tDir.z;
            this.mLineFeedback.updateVerticesData(VertexBuffer.PositionKind, this.mLineFeedbackPosData, true, false);
            this.mLineFeedback.refreshBoundingInfo();
        }
        //update arc feedback
        let tStepSize = PI_MUL2 / 72; //5° steps
        let tSteps = this.mTotalAngle / tStepSize;
        if (tSteps < 0) {
            tSteps = -tSteps;
            tStepSize = -tStepSize;
        }
        const tArcPath1 = new Array();
        const tArcPath2 = new Array();
        const tAddPoint = function (pAngle) {
            const tX = Math.cos(pAngle);
            const tY = Math.sin(pAngle);
            tArcPath1.push(new Vector3(tX, tY, 0));
            tArcPath2.push(new Vector3(tX * 0.9, tY * 0.9, 0));
        };
        for (let i = 0; i < tSteps; ++i) {
            tAddPoint(i * tStepSize + this.mArcFeedbackOffset);
        }
        tAddPoint(this.mTotalAngle + this.mArcFeedbackOffset);
        if (this.mArcFeedback != null && this.mArcFeedbackOverlay != null) {
            if (tArcPath1.length > 1) {
                const tData = VertexData.CreateRibbon({ pathArray: [tArcPath1, tArcPath2] });
                if (tData != null) {
                    tData.applyToMesh(this.mArcFeedback, false);
                    tData.applyToMesh(this.mArcFeedbackOverlay, false);
                    this.mArcFeedback.isVisible = true;
                    this.mArcFeedbackOverlay.isVisible = true;
                }
            }
            else {
                this.mArcFeedback.isVisible = false;
                this.mArcFeedbackOverlay.isVisible = false;
            }
        }
        //update value div
        if (this.mValueDiv != null) {
            const tAngle = radToDeg(Math.abs(this.mTotalAngle));
            this.mValueDiv.textContent = tAngle.toFixed(0) + " °";
            const tProjPos = this.mView.projectPoint2(this.mRefPoint, CoordinatesType.Viewer);
            if (tProjPos != null) {
                this.mValueDiv.style.top = Math.round(tProjPos.y - this.mValueDiv.offsetHeight * 0.5) + "px";
                this.mValueDiv.style.left = Math.round(tProjPos.x - this.mValueDiv.offsetWidth * 0.5) + "px";
            }
        }
    }
    snapOnAxis(pPos) {
        if (pPos == null)
            return (null);
        this.mCurrentSnapAxis = null;
        const tResult = SnapLine.Snap(pPos, this.mSnapAxes, this.mView);
        if (tResult != null) {
            this.mCurrentSnapAxis = tResult.line;
            return (tResult.pos);
        }
        return (pPos);
    }
}
